import React, { useEffect, useState } from "react";
import { Column } from "primereact/column";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import SublineApi from "../../services/SublineApi";
import LineApi from "../../services/LineApi";
import IApiSublinesResponse from "../../interfaces/IApiSublinesResponse";
import { useToast } from "../../context/ToastContext";

// Design System
import PageHeader from "../../components/PageHeader/PageHeader";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import DataTable from "../../components/DataTable/DataTable";
import Input from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import { IconButton } from "../../components/IconButton/IconButton";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import type { PaginatorPageChangeEvent } from "../../components/Paginator/Paginator";

const STORAGE_KEY = "ListSublines_filters";

const ListSublines: React.FC = () => {
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translateForm } = useTranslation("form");
  const { t: translateGeneral } = useTranslation("general");
  const { t: translateMessages } = useTranslation("messages");

  const [filteredSublines, setFilteredSublines] = useState<IApiSublinesResponse[]>([]);
  const [initialData, setInitialData] = useState<IApiSublinesResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Filters state
  const [filterName, setFilterName] = useState("");
  const [filterLines, setFilterLines] = useState<string[]>([]);
  const [lineOptions, setLineOptions] = useState<{ label: string; value: string }[]>([]);

  const [savedFilters, setSavedFilters] = useState<Record<string, any>>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  });

  // Pagination/Sort state (Client-side)
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [sortField, setSortField] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [sublineToDelete, setSublineToDelete] = useState<string | null>(null);

  useEffect(() => {
    fetchSublines();
    fetchLines();
  }, []);

  useEffect(() => {
    if (initialData.length > 0) {
      if (savedFilters.name) setFilterName(savedFilters.name.value);
      if (savedFilters.lines) setFilterLines(savedFilters.lines.value);
      applyFilters(savedFilters);
    }
  }, [initialData]);

  const fetchLines = async (): Promise<void> => {
    try {
      const resp = await LineApi.GetAllLines();
      const options = (resp.data || []).map((l: any) => ({
        label: l.name,
        value: l.id,
      }));
      setLineOptions(options.sort((a: any, b: any) => a.label.localeCompare(b.label)));
    } catch (error) {
      console.error("Error fetching lines:", error);
    }
  };

  const fetchSublines = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await SublineApi.GetAllSublines();
      const fetched = response.data || [];
      setInitialData(fetched);
      setFilteredSublines(fetched);
    } catch (error: any) {
      displayToast("error", translateForm("error"), error.message || error, false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = (filters: Record<string, any>): void => {
    let filtered = [...initialData];

    if (filters.name?.value) {
      filtered = filtered.filter((s) =>
        s.name.toLowerCase().includes(filters.name.value.toLowerCase())
      );
    }

    if (filters.lines?.value && filters.lines.value.length > 0) {
      filtered = filtered.filter((s) =>
        filters.lines.value.includes(s.lineId || s.line?.id)
      );
    }

    setFilteredSublines(filtered);
    setPage(0);
  };

  const handleFilter = (): void => {
    const filters: Record<string, any> = {
      name: filterName ? { value: filterName } : undefined,
      lines: filterLines && filterLines.length > 0 ? { value: filterLines } : undefined,
    };

    setSavedFilters(filters);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    applyFilters(filters);
  };

  const handleClearFilters = (): void => {
    setFilterName("");
    setFilterLines([]);
    setSavedFilters({});
    localStorage.removeItem(STORAGE_KEY);
    setFilteredSublines(initialData);
    setPage(0);
  };

  const deleteSubline = async (id: string): Promise<void> => {
    setIsLoading(true);
    try {
      await SublineApi.DeleteSubline(id);
      displayToast("success", translateMessages("deleteSuccess"), "", false, 3000);
      fetchSublines();
    } catch (error: any) {
      displayToast("error", translateForm("error"), error.message || error, false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDelete = (subline: IApiSublinesResponse): void => {
    setSublineToDelete(subline.id);
    setShowConfirmDelete(true);
  };

  const handlePageChange = (e: PaginatorPageChangeEvent): void => {
    setPage(e.first);
    setRows(e.rows);
  };

  const handleSortChange = (e: any): void => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder === 1 ? "asc" : "desc");
  };

  const actionBodyTemplate = (rowData: IApiSublinesResponse): JSX.Element => (
    <div style={{ display: "flex", gap: "0.25rem", justifyContent: "center", alignItems: "center" }}>
      <IconButton
        icon="visibility"
        variant="text"
        severity="secondary"
        onClick={() => navigate(`/sublines/${rowData.id}`)}
        aria-label={translateGeneral("seeDetails")}
      />
      <IconButton
        icon="edit"
        variant="text"
        severity="secondary"
        onClick={() => navigate(`/sublines/${rowData.id}/edit`)}
        aria-label={translateGeneral("edit")}
      />
      <IconButton
        icon="delete"
        variant="text"
        severity="secondary"
        onClick={() => confirmDelete(rowData)}
        aria-label={translateGeneral("delete")}
      />
    </div>
  );

  return (
    <div className="overflow-hidden">
      <PageHeader
        title={translateForm("sublines")}
        description="Gerencie as sublinhas das linhas de produção"
      >
        <Button
          label={translateGeneral("register")}
          icon="add"
          severity="primary"
          onClick={() => navigate("/sublines/create")}
        />
      </PageHeader>

      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <FilterPanel
          title="Filtros"
          onApply={handleFilter}
          onClear={handleClearFilters}
        >
          <Input
            type="text"
            label={translateForm("name")}
            value={filterName}
            onChange={(val: any) => setFilterName(val)}
          />
          <Input
            type="multiselect"
            label={translateForm("line")}
            value={filterLines}
            options={lineOptions}
            optionLabel="label"
            optionValue="value"
            onChange={(val: any) => setFilterLines(val)}
          />
        </FilterPanel>

        <DataTable
          value={filteredSublines}
          paginated
          first={page}
          rows={rows}
          totalRecords={filteredSublines.length}
          onPageChange={handlePageChange}
          rowsPerPageOptions={[10, 20, 50]}
          loading={isLoading}
          sortField={sortField}
          sortOrder={sortOrder === "asc" ? 1 : -1}
          onSort={handleSortChange}
          emptyMessage={translateGeneral("noRecordsFound")}
        >
          <Column field="name" header={translateForm("name")} sortable />
          <Column field="line.name" header={translateForm("line")} sortable />
          <Column
            header={translateForm("actions")}
            body={actionBodyTemplate}
            style={{ width: "8rem" }}
          />
        </DataTable>
      </div>
      <ConfirmModal
        visible={showConfirmDelete}
        onHide={() => {
          setShowConfirmDelete(false);
          setSublineToDelete(null);
        }}
        onConfirm={() => sublineToDelete && deleteSubline(sublineToDelete)}
        title={translateGeneral("confirmation")}
        message={translateMessages("deleteSubline")}
        severity="danger"
        confirmLabel={translateForm("Yes")}
        cancelLabel={translateForm("No")}
      />
    </div>
  );
};

export default ListSublines;
